// @flow

import { useMemo, useEffect } from 'react';

import { useDispatch } from 'react-redux';

import {
  fetchRenewDurations,
  selectCartCurrency,
  selectCartProductCatalogEntries,
  selectCartProductOptions,
  selectHasRenewDurations,
  selectOneProductById,
  selectRenewDurations,
  type Catalog,
  type DecoratedProduct,
  type DurationUnit,
  type ProductId,
} from '@gandi/cart';

import { useAppSelector } from 'src/hooks/useAppSelector';

function useDomainRenewDurations(product: void | DecoratedProduct) {
  const dispatch = useDispatch();
  const isDomainRenew =
    product?.productType === 'domain' && product.process === 'renew';
  const productId = product?.id;

  const productIdProps = useMemo(() => ({ productId }), [productId]);

  // Fetch renew durations if not provided
  const hasRenewDurations = useAppSelector(state =>
    // TODO: Fix warning in the cart lib
    selectHasRenewDurations(state, productIdProps),
  );

  useEffect(() => {
    if (isDomainRenew && product && !hasRenewDurations) {
      // $FlowFixMe[incompatible-call] refine @gandi/cart Product and DomainProduct
      dispatch(fetchRenewDurations(product));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Don't refetch if product is patched
  }, [isDomainRenew, hasRenewDurations]);

  const productRenewDurations = useAppSelector(state =>
    // TODO: Fix warning in the cart lib
    selectRenewDurations(state, productIdProps),
  );

  return { isDomainRenew, productRenewDurations };
}

function useProductDurations(productId: ProductId): Array<number> {
  const productIdProps = useMemo(() => ({ productId }), [productId]);
  /** The Cart product with its payload (current duration and options) and applicable price. */
  const product = useAppSelector(state =>
    selectOneProductById(state, productIdProps),
  );
  /** Available catalog durations for this domain, indexed by phase. */
  const catalogOptions = useAppSelector(state =>
    selectCartProductOptions(state, productIdProps),
  );
  /** Available durations for this domain, based on its current expiration date. */
  const { isDomainRenew, productRenewDurations } =
    useDomainRenewDurations(product);

  const phase = product?.payload.tld_phase;
  const catalogDurations =
    // $FlowFixMe[incompatible-type] We don't have a phase for renew.
    catalogOptions?.[phase];

  // Prefer the specific product's renew durations if available,
  // as they take into account the current domain's expiration date
  // (don't allow to renew for 9 years if the domain already expires in more than 1 year).
  if (isDomainRenew && productRenewDurations && productRenewDurations.length) {
    return productRenewDurations;
  }

  return catalogDurations && catalogDurations.length > 1
    ? catalogDurations
    : [product?.payload.duration || 1];
}

export type BetterOfferResult = {
  hasBetterOffer: boolean,
  basePercent: number,
  hookPercent: number,
  promoMinDuration: number,
};
/**
 * Returns helpers that can compute the best price offers available for a product.
 */
export function useBestCatalogPrice(productId: ProductId): {
  /** Available durations for the product. */
  durations: Array<number>,
  /** FQDN of the product, for debug purposes. */
  fqdn: string | void,
  /** Normal price of the cart product. */
  priceCrossedOut: number | void,
  /** Total price of the cart product row, before discount codes. */
  total: number | void,
  /** Compute the best price available for the given duration. */
  getBestPrice: (
    duration: number | string,
    unit?: DurationUnit,
  ) => void | Catalog,
  /** Check if there is a special offer on multiyear
   * that is better than the 1-year (or minimal duration's) price.
   *
   * @returns {BetterOfferResult} Object containing:
   * - basePercent: The real global discount percentage. Used for years following the hook.
   * - hookPercent: The boosted discount to display for the starting year.
   */
  findBetterOffer: () => BetterOfferResult,
} {
  const productIdProps = useMemo(() => ({ productId }), [productId]);
  /** The Cart product with its payload (current duration and options) and applicable price. */
  const product = useAppSelector(state =>
    selectOneProductById(state, productIdProps),
  );
  /** The prices catalog for that product (all existing prices,
   * including prices in any duration\
   * and prices that are **not** currently applicable
   * because a better price (like a temporary discount) can be used instead). */
  const productCatalog = useAppSelector(state =>
    // TODO: Fix warning in cart lib
    selectCartProductCatalogEntries(state, productIdProps),
  );

  const currency = useAppSelector(selectCartCurrency);

  const durations = useProductDurations(productId);
  const minDuration = durations?.[0] || product?.payload.duration || 1;

  const price = product && (product.total?.before_taxes || product.unitPrice);
  const total = product?.isWorking ? undefined : price;

  const priceDuration = product?.payload.duration || minDuration;
  const currentPriceCatalog = getBestPrice(
    priceDuration,
    product?.payload.duration_unit,
  );

  const catalogNormalPrice =
    (currentPriceCatalog?.normal_price_before_taxes || 0) * priceDuration;

  const minPriceCatalog = getBestPrice(
    minDuration,
    product?.payload.duration_unit,
  );
  const fallbackNormalPrice =
    (minPriceCatalog?.normal_price_before_taxes ||
      minPriceCatalog?.price_before_taxes ||
      0) * priceDuration;

  const calculatedPriceCrossedOut = Math.max(
    catalogNormalPrice || fallbackNormalPrice,
    product?.total?.before_discount || 0,
  );
  const priceCrossedOut =
    calculatedPriceCrossedOut > (price || 0)
      ? calculatedPriceCrossedOut
      : undefined;

  function getBestPrice(
    duration: number | string | void,
    unit?: string | DurationUnit = 'y',
  ): void | Catalog {
    if (!duration) {
      return undefined;
    }

    const bestPrice = product?.payload.shopping?.best_price?.[currency];
    const allOptions =
      productCatalog.length > 0 || !bestPrice
        ? productCatalog
        : productCatalog.concat(bestPrice);

    return allOptions.reduce<Catalog | void>((best, option) => {
      // filter our options by phase
      if (
        product?.productType === 'domain' &&
        product.payload.tld_phase &&
        option.options.phase &&
        option.options.phase !== product?.payload.tld_phase
      ) {
        // skip options that don't match the phase
        return best;
      }

      // filter out options that don't match the duration
      if (
        unit === option.duration_unit &&
        parseInt(duration, 10) >= option.min_duration &&
        parseInt(duration, 10) <= option.max_duration
      ) {
        // find the best price
        return !best || option.price_before_taxes < best.price_before_taxes
          ? option
          : best;
      }

      return best;
    }, undefined);
  }

  function findBetterOffer() {
    const firstPrice = getBestPrice(
      minDuration,
      product?.payload.duration_unit,
    );
    const firstPriceAmount = firstPrice?.price_before_taxes;

    const bestOffer = productCatalog.reduce<BetterOfferResult>(
      (best, option) => {
        if (
          option.min_duration > minDuration &&
          typeof firstPriceAmount === 'number'
        ) {
          const percentDifference =
            ((firstPriceAmount - option.price_before_taxes) /
              firstPriceAmount) *
            100;

          const basePercent = Math.trunc(percentDifference);
          // Calculate the hook discount absorbed by the first eligible year
          const hookPercent = basePercent * option.min_duration;

          if (
            basePercent > 0 &&
            (!best.hasBetterOffer || percentDifference > best.basePercent)
          ) {
            return {
              hasBetterOffer: true,
              basePercent,
              hookPercent,
              promoMinDuration: option.min_duration,
            };
          }
        }
        return best;
      },
      {
        hasBetterOffer: false,
        basePercent: 0,
        hookPercent: 0,
        promoMinDuration: minDuration,
      },
    );

    return bestOffer;
  }

  return {
    durations,
    fqdn: product?.payload.fqdn || product?.identifier,
    getBestPrice,
    findBetterOffer,
    priceCrossedOut,
    total,
  };
}
